/**
 * ImageManager.jsx
 *
 * Upload rules are enforced in frontend for instant feedback and in backend for real security.
 * Allowed: JPG, JPEG, PNG, WEBP, SVG
 * Blocked: GIF, AVIF
 * Max upload: 20 images at once, each image less than 1 MB
 */

import { useState, useEffect, useCallback, useRef } from "react";

const LIMIT = 40;
const MAX_UPLOAD_FILES = 20;
const MAX_IMAGE_BYTES = 1024 * 1024;
const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".svg"];
const ALLOWED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/svg+xml"];
const BLOCKED_EXTENSIONS = [".gif", ".avif"];
const BLOCKED_MIME_TYPES = ["image/gif", "image/avif"];
const FILE_ACCEPT = ".jpg,.jpeg,.png,.webp,.svg,image/jpeg,image/png,image/webp,image/svg+xml";

function formatBytes(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function fileExt(name = "") {
  const dot = String(name).lastIndexOf(".");
  return dot >= 0 ? String(name).slice(dot).toLowerCase() : "";
}

function Spinner() {
  return (
    <svg className="spin" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

export default function ImageManager({
  baseUrl = "http://localhost:6006",
  authToken = "",
}) {
  const api = baseUrl.replace(/\/$/, "");

  const [images, setImages] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [renamingFilename, setRenamingFilename] = useState(null);
  const [renameModal, setRenameModal] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState([]);
  const [selectedImages, setSelectedImages] = useState(() => new Set());
  const [alertModal, setAlertModal] = useState(null);

  const fileInputRef = useRef(null);
  const searchTimer = useRef(null);

  const selectedCount = selectedImages.size;
  const visibleFilenames = images.map((img) => img.filename);
  const allVisibleSelected = visibleFilenames.length > 0 && visibleFilenames.every((filename) => selectedImages.has(filename));

  function showAlert(title, message, variant = "error") {
    setAlertModal({ title, message, variant });
  }

  function parseResponse(res) {
    return res.json().catch(() => ({ success: false, message: `Request failed with status ${res.status}` }));
  }

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (debouncedSearch) params.set("search", debouncedSearch);

      const res = await fetch(`${api}/api/admin/image-manager?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await parseResponse(res);

      if (!res.ok || !data.success) throw new Error(data.message || "Failed to fetch images");

      setImages(data.images || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (e) {
      const message = e.message || "Could not load images";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [api, authToken, page, debouncedSearch]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  function validateUploadFiles(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return null;

    if (files.length > MAX_UPLOAD_FILES) {
      showAlert(
        "Upload limit exceeded",
        `Maximum ${MAX_UPLOAD_FILES} images can be uploaded at once. You selected ${files.length} images.`
      );
      return null;
    }

    const blocked = files.find((file) => {
      const ext = fileExt(file.name);
      const mime = String(file.type || "").toLowerCase();
      return BLOCKED_EXTENSIONS.includes(ext) || BLOCKED_MIME_TYPES.includes(mime);
    });

    if (blocked) {
      showAlert(
        "Image type blocked",
        `${blocked.name} is not allowed. GIF and AVIF support is blocked. Upload JPG, PNG, WEBP or SVG only.`
      );
      return null;
    }

    const unsupported = files.find((file) => {
      const ext = fileExt(file.name);
      const mime = String(file.type || "").toLowerCase();
      return !ALLOWED_EXTENSIONS.includes(ext) || (mime && !ALLOWED_MIME_TYPES.includes(mime));
    });

    if (unsupported) {
      showAlert(
        "Invalid image type",
        `${unsupported.name} is not supported. Only JPG, JPEG, PNG, WEBP and SVG images are allowed.`
      );
      return null;
    }

    const tooLarge = files.find((file) => Number(file.size || 0) > MAX_IMAGE_BYTES);
    if (tooLarge) {
      showAlert(
        "Image size too large",
        `${tooLarge.name} is ${formatBytes(tooLarge.size)}. Every image must be less than 1 MB.`
      );
      return null;
    }

    return files;
  }

  async function handleUpload(fileList) {
    const files = validateUploadFiles(fileList);
    if (!files) return;

    setUploading(true);
    setUploadProgress(files.map((f) => ({ name: f.name, status: "uploading" })));
    setError(null);

    const form = new FormData();
    files.forEach((f) => form.append("images", f));

    try {
      const res = await fetch(`${api}/api/admin/image-manager/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
        body: form,
      });
      const data = await parseResponse(res);

      if (!res.ok || !data.success) throw new Error(data.message || "Upload failed");

      setUploadProgress(files.map((f) => ({ name: f.name, status: "done" })));
      setSuccessMsg(`${data.count} image${data.count !== 1 ? "s" : ""} uploaded successfully`);
      setTimeout(() => setSuccessMsg(null), 3500);
      setPage(1);
      await fetchImages();
    } catch (e) {
      const message = e.message || "Upload failed";
      setError(message);
      showAlert("Upload failed", message);
      setUploadProgress([]);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress([]), 2000);
    }
  }

  function onFileInput(e) {
    handleUpload(e.target.files);
    e.target.value = "";
  }

  function onDrop(e) {
    e.preventDefault();
    setIsDragging(false);
    handleUpload(e.dataTransfer.files);
  }

  function toggleSelected(filename) {
    setSelectedImages((prev) => {
      const next = new Set(prev);
      if (next.has(filename)) next.delete(filename);
      else next.add(filename);
      return next;
    });
  }

  function selectAllVisible() {
    setSelectedImages((prev) => {
      const next = new Set(prev);
      visibleFilenames.forEach((filename) => next.add(filename));
      return next;
    });
  }

  function clearVisibleSelection() {
    setSelectedImages((prev) => {
      const next = new Set(prev);
      visibleFilenames.forEach((filename) => next.delete(filename));
      return next;
    });
  }

  function clearAllSelection() {
    setSelectedImages(new Set());
  }

  function openDeleteModal(filenames) {
    const targets = Array.isArray(filenames) ? filenames.filter(Boolean) : [filenames].filter(Boolean);
    if (!targets.length) {
      showAlert("No image selected", "Select at least one image before deleting.");
      return;
    }
    setDeleteTargets(targets);
    setDeleteModal(true);
  }

  async function handleDelete() {
    if (!deleteTargets.length) return;

    setDeleting(true);
    setError(null);

    try {
      const isBulk = deleteTargets.length > 1;
      const res = await fetch(
        isBulk
          ? `${api}/api/admin/image-manager/bulk`
          : `${api}/api/admin/image-manager/${encodeURIComponent(deleteTargets[0])}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            ...(isBulk ? { "Content-Type": "application/json" } : {}),
          },
          ...(isBulk ? { body: JSON.stringify({ filenames: deleteTargets }) } : {}),
        }
      );
      const data = await parseResponse(res);

      if (!res.ok || !data.success) throw new Error(data.message || "Delete failed");

      const deletedSet = new Set(isBulk ? data.deleted || deleteTargets : deleteTargets);
      setImages((prev) => prev.filter((img) => !deletedSet.has(img.filename)));
      setSelectedImages((prev) => {
        const next = new Set(prev);
        deletedSet.forEach((filename) => next.delete(filename));
        return next;
      });
      setTotal((t) => Math.max(0, t - deletedSet.size));
      setDeleteModal(false);
      setDeleteTargets([]);
      setSuccessMsg(`${deletedSet.size} image${deletedSet.size === 1 ? "" : "s"} deleted successfully`);
      setTimeout(() => setSuccessMsg(null), 3000);
      await fetchImages();
    } catch (e) {
      const message = e.message || "Delete failed";
      setError(message);
      showAlert("Delete failed", message);
    } finally {
      setDeleting(false);
    }
  }

  function openRenameModal(filename) {
    const currentName = filename.replace(/\.[^/.]+$/, "");
    setRenameTarget(filename);
    setRenameValue(currentName);
    setRenameModal(true);
  }

  async function handleRename() {
    if (!renameTarget) return;

    const ext = renameTarget.split(".").pop();
    const rawValue = renameValue.trim();
    const cleanBase = rawValue.replace(new RegExp(`\\.${ext}$`, "i"), "").trim();

    if (!cleanBase) {
      showAlert("Rename required", "Enter a valid image name before saving.");
      return;
    }

    if (cleanBase.includes("..") || cleanBase.includes("/") || cleanBase.includes("\\")) {
      showAlert("Invalid image name", "Image name cannot contain /, \\ or .. characters.");
      return;
    }

    const finalFilename = `${cleanBase}.${ext}`;
    setRenamingFilename(renameTarget);
    setError(null);

    try {
      const res = await fetch(`${api}/api/admin/image-manager/${encodeURIComponent(renameTarget)}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ newFilename: finalFilename }),
      });
      const data = await parseResponse(res);

      if (!res.ok || !data.success) throw new Error(data.message || "Rename failed");

      setRenameModal(false);
      setRenameTarget(null);
      setSuccessMsg("Image renamed successfully");
      setTimeout(() => setSuccessMsg(null), 3000);
      await fetchImages();
    } catch (e) {
      const message = e.message || "Rename failed";
      setError(message);
      showAlert("Rename failed", message);
    } finally {
      setRenamingFilename(null);
    }
  }

  function copyUrl(fullUrl, filename) {
    navigator.clipboard
      .writeText(fullUrl)
      .then(() => {
        setCopiedUrl(filename);
        setTimeout(() => setCopiedUrl(null), 2000);
      })
      .catch(() => showAlert("Copy failed", "Could not copy this image URL. Please copy it manually."));
  }

  return (
    <>
      <style>{`
        .im-root {
          --im-primary: #d86a78;
          --im-primary-dark: #c75a69;
          --im-green: #16a34a;
          --im-green-light: #22c55e;
          --im-gradient: #c75a69;
          font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif;
          background: #f8f9fb;
          min-height: 100vh;
          color: #1a1d23;
        }
        .im-header {
          background: #fff;
          border-bottom: 1px solid #e5e7ed;
          padding: 18px 28px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .im-title { font-size: 22px; font-weight: 700; letter-spacing: -0.3px; color:#dc667b; margin: 0; }
        .im-subtitle { font-size: 13px; color: #6b7280; margin: 2px 0 0 0; }
        .im-header-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .im-search {
          padding: 8px 14px;
          border: 1px solid #e0e2e8;
          border-radius: 8px;
          font-size: 13.5px;
          width: 220px;
          background: #f8f9fb;
          outline: none;
          transition: border 0.15s, box-shadow 0.15s;
        }
        .im-search:focus { border-color: var(--im-primary); box-shadow: 0 0 0 3px rgba(216,106,120,0.14); background: #fff; }
        .im-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 9px;
          font-size: 13.5px;
          font-weight: 700;
          cursor: pointer;
          border: none;
          transition: transform 0.1s, filter 0.15s, box-shadow 0.15s, background 0.15s;
          white-space: nowrap;
        }
        .im-btn:active { transform: scale(0.97); }
        .im-btn-primary { background: var(--im-gradient); color: #fff; box-shadow: 0 8px 20px rgba(216,106,120,0.22); }
        .im-btn-primary:hover { filter: brightness(0.96); box-shadow: 0 10px 24px rgba(22,163,74,0.18); }
        .im-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; box-shadow: none; }
        .im-btn-soft { background: #fff; color: #334155; border: 1px solid #e2e8f0; }
        .im-btn-soft:hover { background: #f8fafc; border-color: #cbd5e1; }
        .im-btn-danger { background: linear-gradient(135deg, #d86a78, #e11d48); color: #fff; box-shadow: 0 8px 20px rgba(225,29,72,0.15); }
        .im-btn-danger:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
        .im-body { padding: 24px 28px; }
        .im-drop-zone {
          border: 2px dashed #dec0c6;
          border-radius: 16px;
          padding: 36px;
          text-align: center;
          background: #fff;
          margin-bottom: 18px;
          transition: border-color 0.15s, background 0.15s, transform 0.15s;
          cursor: pointer;
        }
        .im-drop-zone.dragging { border-color: var(--im-green); background: #f0fdf4; transform: translateY(-1px); }
        .im-drop-zone:hover { border-color: var(--im-primary); }
        .im-drop-icon { display:inline-block; color: var(--im-primary); margin-bottom: 10px; }
        .im-drop-label { font-size: 14px; color: #374151; font-weight: 600; }
        .im-drop-sub { font-size: 12.5px; color: #9ca3af; margin-top: 4px; }
        .im-selection-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
          background: #fff;
          border: 1px solid #f0d6db;
          border-radius: 14px;
          padding: 12px 14px;
          margin-bottom: 16px;
          box-shadow: 0 6px 18px rgba(15,23,42,0.04);
        }
        .im-selection-left { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .im-selection-count { font-size: 13px; color: #475569; }
        .im-selection-count strong { color: #111827; }
        .im-progress { margin-bottom: 16px; display: flex; flex-direction: column; gap: 6px; }
        .im-progress-item { display: flex; align-items: center; gap: 8px; font-size: 12.5px; padding: 6px 12px; border-radius: 6px; background: #f1f5f9; }
        .im-progress-done { background: #ecfdf5; color: #059669; }
        .im-toast { padding: 11px 16px; border-radius: 8px; font-size: 13.5px; font-weight: 600; margin-bottom: 18px; display: flex; align-items: center; gap: 8px; }
        .im-toast-success { background: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; }
        .im-toast-error { background: #fff1f2; color: #9f1239; border: 1px solid #fecdd3; }
        .im-stats { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; flex-wrap: wrap; gap: 8px; }
        .im-count { font-size: 13px; color: #6b7280; }
        .im-count strong { color: #111; }
        .im-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
        .im-card {
          position: relative;
          background: #fff;
          border-radius: 12px;
          border: 1px solid #e5e7ed;
          overflow: hidden;
          transition: box-shadow 0.15s, border-color 0.15s, transform 0.15s;
          display: flex;
          flex-direction: column;
        }
        .im-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.09); border-color: #dec0c6; transform: translateY(-1px); }
        .im-card.selected { border-color: var(--im-primary); box-shadow: 0 0 0 3px rgba(216,106,120,0.14); }
        .im-select-box {
          position: absolute;
          top: 10px;
          left: 10px;
          z-index: 2;
          width: 26px;
          height: 26px;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.9);
          background: rgba(255,255,255,0.94);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 5px 14px rgba(0,0,0,0.16);
          cursor: pointer;
        }
        .im-select-box input { width: 15px; height: 15px; accent-color: var(--im-primary); cursor: pointer; }
        .im-thumb-wrap { width: 100%; height: 150px; background: #f0f2f5; display: flex; align-items: center; justify-content: center; overflow: hidden; cursor: pointer; position: relative; }
        .im-thumb { width: 100%; height: 100%; object-fit: cover; transition: transform 0.2s; }
        .im-thumb-wrap:hover .im-thumb { transform: scale(1.04); }
        .im-card-body { padding: 10px 12px; flex: 1; display: flex; flex-direction: column; gap: 6px; }
        .im-filename { font-size: 11.5px; color: #374151; word-break: break-all; line-height: 1.4; font-weight: 600; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .im-meta { display: flex; align-items: center; gap: 6px; font-size: 11px; color: #9ca3af; flex-wrap: wrap; }
        .im-url-row { display: flex; align-items: center; gap: 4px; background: #f3f4f8; border-radius: 5px; padding: 4px 7px; font-size: 10.5px; color: #4b5563; font-family: 'Menlo', 'Consolas', monospace; overflow: hidden; }
        .im-url-text { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .im-actions { display: flex; align-items: center; gap: 5px; margin-top: 2px; }
        .im-icon-btn { display: inline-flex; align-items: center; justify-content: center; gap: 4px; padding: 6px 8px; border-radius: 6px; border: 1px solid #e5e7ed; background: #fff; color: #6b7280; cursor: pointer; font-size: 11.5px; font-weight: 600; transition: background 0.15s, color 0.15s, border-color 0.15s; }
        .im-icon-btn:hover { background: #f9fafb; color: #111; }
        .im-icon-btn-copy { flex: 1; }
        .im-icon-btn-copy.copied { background: #ecfdf5; color: #059669; border-color: #a7f3d0; }
        .im-icon-btn-open:hover { color: var(--im-primary); border-color: #f0cbd1; }
        .im-icon-btn-del:hover { color: #dc2626; border-color: #fecaca; background: #fef2f2; }
        .im-icon-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .im-pagination { display: flex; justify-content: center; align-items: center; gap: 6px; margin-top: 28px; flex-wrap: wrap; }
        .im-page-btn { padding: 7px 11px; border-radius: 7px; border: 1px solid #e5e7ed; background: #fff; color: #374151; font-size: 13px; cursor: pointer; }
        .im-page-btn:hover:not(:disabled) { background: #fff1f2; border-color: #f0cbd1; }
        .im-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .im-page-btn.active { background: var(--im-gradient); color: #fff; border-color: transparent; }
        .im-empty { text-align: center; padding: 56px 20px; color: #9ca3af; }
        .im-empty-icon { font-size: 40px; margin-bottom: 12px; }
        .im-empty-text { font-size: 15px; font-weight: 700; color: #374151; }
        .im-empty-sub { font-size: 13px; color: #9ca3af; margin-top: 4px; }
        .im-skeleton { background: linear-gradient(90deg, #f0f2f5 25%, #e5e7ed 50%, #f0f2f5 75%); background-size: 200% 100%; animation: shimmer 1.4s infinite; border-radius: 8px; }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        .im-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.58); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 24px; }
        .im-lightbox-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.82); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 24px; }
        .im-overlay-img { max-width: 90vw; max-height: 88vh; border-radius: 10px; box-shadow: 0 24px 80px rgba(0,0,0,0.5); }
        .im-overlay-close { position: fixed; top: 20px; right: 24px; background: rgba(255,255,255,0.15); color: #fff; border: none; border-radius: 50%; width: 38px; height: 38px; font-size: 18px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.15s; }
        .im-overlay-close:hover { background: rgba(255,255,255,0.25); }
        .spin { animation: spin 0.8s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .im-ext-badge { display: inline-block; padding: 1px 5px; border-radius: 4px; font-size: 10px; font-weight: 800; text-transform: uppercase; background: #f0f2f5; color: #6b7280; letter-spacing: 0.3px; }
        .im-modal { background: #fff; width: 420px; max-width: 95%; border-radius: 16px; padding: 22px; box-shadow: 0 20px 60px rgba(0,0,0,.25); }
        .im-modal-icon { width: 42px; height: 42px; border-radius: 14px; display: flex; align-items: center; justify-content: center; margin-bottom: 12px; font-size: 20px; }
        .im-modal-icon.error { background: #fff1f2; color: #e11d48; }
        .im-modal-icon.success { background: #ecfdf5; color: #16a34a; }
        .im-modal-title { font-size: 18px; font-weight: 800; margin-bottom: 8px; color: #111827; }
        .im-modal-text { font-size: 14px; color: #6b7280; margin-bottom: 16px; line-height: 1.55; }
        .im-modal-input { width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; outline: none; font-size: 14px; }
        .im-modal-input:focus { border-color: var(--im-primary); box-shadow: 0 0 0 3px rgba(216,106,120,0.12); }
        .im-modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 18px; }
        .im-modal-btn { border: none; padding: 10px 16px; border-radius: 8px; cursor: pointer; font-weight: 700; }
        .im-modal-cancel { background: #f3f4f6; color: #374151; }
        .im-modal-primary { background: var(--im-gradient); color: white; }
        .im-modal-danger { background: linear-gradient(135deg, #d86a78, #e11d48); color: white; }
        .im-modal-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        @media (max-width: 640px) {
          .im-header, .im-body { padding-left: 16px; padding-right: 16px; }
          .im-search { width: 100%; }
          .im-header-actions { width: 100%; }
          .im-header-actions .im-btn { flex: 1; }
          .im-drop-zone { padding: 26px 16px; }
          .im-grid { grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); }
        }
      `}</style>

      <div className="im-root">
        <div className="im-header">
          <div>
            <h1 className="im-title">Image Manager</h1>
            <p className="im-subtitle">Upload and manage all product images from one place</p>
          </div>
          <div className="im-header-actions">
            <input className="im-search" placeholder="Search images…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <button className="im-btn im-btn-primary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              {uploading ? <Spinner /> : <UploadIcon />}
              {uploading ? "Uploading…" : "Upload Images"}
            </button>
            <input ref={fileInputRef} type="file" multiple accept={FILE_ACCEPT} style={{ display: "none" }} onChange={onFileInput} />
          </div>
        </div>

        <div className="im-body">
          <div
            className={`im-drop-zone ${isDragging ? "dragging" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="im-drop-icon"><UploadIcon /></div>
            <div className="im-drop-label">Drag & drop images here, or click to select</div>
            <div className="im-drop-sub">JPG, PNG, WEBP, SVG only • Max 20 images • Less than 1 MB each • GIF/AVIF blocked</div>
          </div>

          <div className="im-selection-bar">
            <div className="im-selection-left">
              <span className="im-selection-count"><strong>{selectedCount}</strong> selected</span>
              <button type="button" className="im-btn im-btn-soft" onClick={allVisibleSelected ? clearVisibleSelection : selectAllVisible} disabled={!images.length}>
                {allVisibleSelected ? "Unselect page" : "Select page"}
              </button>
              {selectedCount > 0 && (
                <button type="button" className="im-btn im-btn-soft" onClick={clearAllSelection}>Clear all</button>
              )}
            </div>
            <button type="button" className="im-btn im-btn-danger" onClick={() => openDeleteModal(Array.from(selectedImages))} disabled={!selectedCount || deleting}>
              {deleting ? <Spinner /> : <TrashIcon />}
              Delete selected
            </button>
          </div>

          {uploadProgress.length > 0 && (
            <div className="im-progress">
              {uploadProgress.map((f, i) => (
                <div key={`${f.name}-${i}`} className={`im-progress-item ${f.status === "done" ? "im-progress-done" : ""}`}>
                  {f.status === "uploading" ? <Spinner /> : "✓"}
                  <span>{f.name}</span>
                </div>
              ))}
            </div>
          )}

          {successMsg && <div className="im-toast im-toast-success">✓ {successMsg}</div>}
          {error && <div className="im-toast im-toast-error">⚠ {error}</div>}

          <div className="im-stats">
            <span className="im-count">
              {loading ? "Loading…" : (
                <>
                  <strong>{total}</strong> image{total !== 1 ? "s" : ""}
                  {debouncedSearch && ` matching "${debouncedSearch}"`}
                </>
              )}
            </span>
            {totalPages > 1 && <span className="im-count">Page <strong>{page}</strong> of <strong>{totalPages}</strong></span>}
          </div>

          {loading ? (
            <div className="im-grid">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #e5e7ed" }}>
                  <div className="im-skeleton" style={{ height: 150 }} />
                  <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
                    <div className="im-skeleton" style={{ height: 12, width: "80%" }} />
                    <div className="im-skeleton" style={{ height: 10, width: "50%" }} />
                    <div className="im-skeleton" style={{ height: 24, marginTop: 4 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : images.length === 0 ? (
            <div className="im-empty">
              <div className="im-empty-icon">🖼️</div>
              <div className="im-empty-text">{debouncedSearch ? `No images match "${debouncedSearch}"` : "No images yet"}</div>
              <div className="im-empty-sub">{debouncedSearch ? "Try a different search term" : "Upload some images to get started"}</div>
            </div>
          ) : (
            <div className="im-grid">
              {images.map((img) => {
                const isSelected = selectedImages.has(img.filename);
                return (
                  <div className={`im-card ${isSelected ? "selected" : ""}`} key={img.filename}>
                    <label className="im-select-box" title="Select image" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSelected(img.filename)} />
                    </label>
                    <div className="im-thumb-wrap" onClick={() => setPreviewImage(img)} title="Click to preview">
                      <img
                        className="im-thumb"
                        src={img.fullUrl}
                        alt={img.filename}
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const parent = e.currentTarget.parentNode;
                          if (parent && !parent.querySelector(".im-load-failed")) {
                            const failed = document.createElement("span");
                            failed.className = "im-load-failed";
                            failed.style.color = "#9ca3af";
                            failed.style.fontSize = "12px";
                            failed.textContent = "Failed to load";
                            parent.appendChild(failed);
                          }
                        }}
                      />
                    </div>
                    <div className="im-card-body">
                      {/* <div className="im-filename">{img.filename}</div> */}
                      <div className="im-meta">
                        <span className="im-ext-badge">{img.ext}</span>
                        <span>{formatBytes(img.size)}</span>
                        <span>·</span>
                        <span>{formatDate(img.uploadedAt)}</span>
                      </div>
                      {/* <div className="im-url-row" title={img.fullUrl}>
                        <span className="im-url-text">{img.url}</span>
                      </div> */}
                      <div className="im-actions">
                        <button className={`im-icon-btn im-icon-btn-copy ${copiedUrl === img.filename ? "copied" : ""}`} onClick={() => copyUrl(img.fullUrl, img.filename)} title="Copy full URL">
                          <CopyIcon />
                          {copiedUrl === img.filename ? "Copied!" : "Copy URL"}
                        </button>
                        <button className="im-icon-btn im-icon-btn-open" onClick={() => window.open(img.fullUrl, "_blank")} title="Open in new tab">
                          <ExternalIcon />
                        </button>
                        <button className="im-icon-btn im-icon-btn-open" onClick={() => openRenameModal(img.filename)} disabled={renamingFilename === img.filename} title="Rename image">
                          {renamingFilename === img.filename ? <Spinner /> : <EditIcon />}
                        </button>
                        <button className="im-icon-btn im-icon-btn-del" onClick={() => openDeleteModal(img.filename)} disabled={deleting} title="Delete image">
                          {deleting && deleteTargets.includes(img.filename) ? <Spinner /> : <TrashIcon />}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {totalPages > 1 && !loading && (
            <div className="im-pagination">
              <button className="im-page-btn" disabled={page <= 1} onClick={() => setPage(1)}>«</button>
              <button className="im-page-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const mid = Math.min(Math.max(page, 3), totalPages - 2);
                const pg = i + mid - 2;
                if (pg < 1 || pg > totalPages) return null;
                return <button key={pg} className={`im-page-btn ${page === pg ? "active" : ""}`} onClick={() => setPage(pg)}>{pg}</button>;
              })}
              <button className="im-page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next ›</button>
              <button className="im-page-btn" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</button>
            </div>
          )}
        </div>
      </div>

      {alertModal && (
        <div className="im-modal-overlay" onClick={() => setAlertModal(null)}>
          <div className="im-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`im-modal-icon ${alertModal.variant === "success" ? "success" : "error"}`}>{alertModal.variant === "success" ? "✓" : "!"}</div>
            <div className="im-modal-title">{alertModal.title}</div>
            <div className="im-modal-text">{alertModal.message}</div>
            <div className="im-modal-actions">
              <button className="im-modal-btn im-modal-primary" onClick={() => setAlertModal(null)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {renameModal && (
        <div className="im-modal-overlay" onClick={() => setRenameModal(false)}>
          <div className="im-modal" onClick={(e) => e.stopPropagation()}>
            <div className="im-modal-title">Rename Image</div>
            <div className="im-modal-text">Only the name changes. The original image extension stays the same.</div>
            <input className="im-modal-input" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus />
            <div className="im-modal-actions">
              <button className="im-modal-btn im-modal-cancel" onClick={() => setRenameModal(false)}>Cancel</button>
              <button className="im-modal-btn im-modal-primary" onClick={handleRename} disabled={!!renamingFilename}>{renamingFilename ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}

      {deleteModal && (
        <div className="im-modal-overlay" onClick={() => !deleting && setDeleteModal(false)}>
          <div className="im-modal" onClick={(e) => e.stopPropagation()}>
            <div className="im-modal-title">Delete Image{deleteTargets.length > 1 ? "s" : ""}</div>
            <div className="im-modal-text">
              {deleteTargets.length > 1
                ? `${deleteTargets.length} selected images will be deleted. This action cannot be undone.`
                : `This image will be deleted. This action cannot be undone.`}
            </div>
            <div className="im-modal-actions">
              <button className="im-modal-btn im-modal-cancel" onClick={() => setDeleteModal(false)} disabled={deleting}>Cancel</button>
              <button className="im-modal-btn im-modal-danger" onClick={handleDelete} disabled={deleting}>{deleting ? "Deleting…" : "Delete"}</button>
            </div>
          </div>
        </div>
      )}

      {previewImage && (
        <div className="im-lightbox-overlay" onClick={() => setPreviewImage(null)}>
          <img className="im-overlay-img" src={previewImage.fullUrl} alt={previewImage.filename} onClick={(e) => e.stopPropagation()} />
          <button className="im-overlay-close" onClick={() => setPreviewImage(null)}>✕</button>
        </div>
      )}
    </>
  );
}
